import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { RootState, isCertificateApplied } from '@share/utils';
import { IReviewBookState } from '@store/slices';

import { IFiltersState, ILoginState } from '@share/store/slices';
import { get } from 'lodash';
import { NON_REFUNDABLE_TYPE, REFUNDABLE_TYPE } from '@constants';
import { Popover } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { ModalCancellationPolicies } from '../../common';

import './style.scss';

interface IMapStateToProps {
  reviewBookStore: IReviewBookState;
  loginStore: ILoginState;
  filtersStore: IFiltersState;
}

interface IProps extends IMapStateToProps, WrappedComponentProps {}

interface IState {
  isModalCancellationPoliciesVisible: boolean;
  isChangedCancellationPolicy: false;
}

class RefundabilityComponent extends React.Component<IProps, IState> {
  
  state: IState = { isChangedCancellationPolicy: false, isModalCancellationPoliciesVisible: false };

  showCancellationPolicies = (): void => {
    this.setState({ isModalCancellationPoliciesVisible: true });
  };

  render(): React.ReactNode {
    const { isModalCancellationPoliciesVisible, isChangedCancellationPolicy } = this.state;
    const { reviewBookStore, loginStore, filtersStore, intl } = this.props;
    const { account, userWallet } = loginStore;

    const certificateApplied = isCertificateApplied(account, filtersStore.certificateId);
    const { bookingSummary, selectedHotelReviewClientCash, bookingComplete, booking } = reviewBookStore;
    const { cancellationPolicyText, refundabilityText } = bookingSummary || {};
    const { refundability } = bookingSummary?.bookingCard?.package || {};

    const hasClientCash = account?.hasClientCash;
    const walletIsExternal = account?.walletIsExternal;
    const walletSkipNonRefundable = account?.walletSkipNonRefundable;

    const isLogged = !!userWallet;

    const usedClientCash = (bookingComplete && booking) ? get(booking?.hotelSegments, '[0].clientCash', 0) : 0;

    // The badge and the modal it opens must agree. Previously only the badge applied the wallet
    // override, so clicking "Non-refundable" opened a modal still listing the rate's original
    // (refundable) policies. Mirrors cancellationPolicyTextFinal in ../review-book/cancellation-policies.
    const forceNonRefundable =
      !walletSkipNonRefundable &&
      !walletIsExternal &&
      hasClientCash &&
      isLogged &&
      (selectedHotelReviewClientCash > 0 || usedClientCash > 0);

    const refundabilityBeforeCertificate = forceNonRefundable ? NON_REFUNDABLE_TYPE : refundability;

    const refundabilityFinal = certificateApplied
      ? NON_REFUNDABLE_TYPE
      : refundabilityBeforeCertificate;

    const showAsNonRefundable = forceNonRefundable || certificateApplied;

    const cancellationPolicyTextFinal = showAsNonRefundable
      ? intl.formatMessage({ id: 'cancellation.not_refundable.message.policy' })
      : cancellationPolicyText;

    // The provider's refundability prose ("refundable until ...") contradicts a booking that the
    // wallet or certificate has forced non-refundable, so hide that block rather than show both.
    const refundabilityTextFinal = showAsNonRefundable ? undefined : refundabilityText;
    const refundabilityClassName = `room-info__refundability ${NON_REFUNDABLE_TYPE === refundabilityFinal ? 'non-refundable' : 'refundable'}`;

    return (
      <>
        <div className={refundabilityClassName} onClick={this.showCancellationPolicies}>
          {NON_REFUNDABLE_TYPE === refundabilityFinal && (
            <p className="room-info__room-info-title"><FormattedMessage id={'non.refundable'} /></p>)}

          {REFUNDABLE_TYPE === refundabilityFinal && (
            <p className="room-info__room-info-title"><FormattedMessage id={'refundable'} /></p>)}

          <Popover
            overlayClassName="overlay-popover"
            content={<FormattedMessage id="changed.cancellation.policy" />}
            open={isChangedCancellationPolicy}
            placement="bottomLeft"
          >
            <FontAwesomeIcon icon={faInfoCircle} size="1x" />
          </Popover>
        </div>

        <ModalCancellationPolicies
          cancellationPoliciesText={cancellationPolicyTextFinal}
          refundabilityText={refundabilityTextFinal}
          visible={isModalCancellationPoliciesVisible}
          onCancel={() => this.setState({ isModalCancellationPoliciesVisible: false })}
        />
      </>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    reviewBookStore: state.reviewBookStore,
    loginStore: state.loginStore,
    filtersStore: state.filtersStore,
  };
};

export const Refundability = connect(mapStateToProps)(injectIntl(RefundabilityComponent));
